import { memo, type ReactNode, useCallback } from 'react';

import { useAirModal } from '@air/provider-modal';

import { PublicShowcaseAnonymousSignUpModal } from '~/components/PublicLinkAnonymousSignUpModal/PublicShowcaseAnonymousSignUpModal';
import { useOnMissingUserSession } from '~/hooks/useOnMissingUserSession';

export type PublicShowcaseRequireAccountGateProps = {
  // The collection tree this gate is about. ALWAYS rendered — see the note above the component.
  children: ReactNode;
  // Whether the link stores "Require name/email to view". Required (never `?`) because a silently
  // absent access gate is the failure this component exists to prevent: a new surface that mounts
  // the collection tree must state its answer, and forgetting is a type error.
  requireAccount: boolean;
};

// The collection share link's "Require name/email to view" gate — the collection twin of the board's
// gate in PublicBoardPage.
//
// CHILDREN RENDER UNCONDITIONALLY. This wrapper never withholds the collection: it probes the
// session on mount and, when the viewer has none, opens the non-dismissible anonymous sign-up modal
// OVER content that is already mounted. That is the product's intent — a soft identity-capture gate,
// not an authorization boundary — and it matches the board precedent this is modeled on. Do not read
// the wrapper shape as conditional rendering; nothing here branches on the session. Closing the
// modal is the entire unlock, because there was never anything to unmount.
//
// The wrapper shape is a declaration of scope, not a mechanism: it names which subtree the gate is
// about, so the access question is legible at the tree it concerns instead of hiding in a childless
// sibling. It costs nothing over that sibling, and it cannot grow into a re-render cost either —
// `children` arrives as an already-created element from the layout's render, so any state added
// inside this component re-renders only this component, and React bails out on the identical child
// element rather than re-rendering the collection tree.
//
// Mounted inside PublicShowcaseLayout rather than by each surface because the modal machinery it
// needs (ModalProvider, ReCaptchaProvider, PublicAccountProvider, ShortIdProvider) lives inside that
// layout for collections — unlike boards, where those providers sit above the page component. The
// gate is opt-in: the two PRIVATE surfaces that mount the collection tree (ShowcasePreview's
// owner-only preview and the editor's inline canvas) pass `requireAccount={false}`, because those
// surfaces apply no access gates at all.
//
// No page-view-notification suppression here (the board's gate pairs with one in
// PublicBoardPage — see its `sendPublicPageViewEvent` effect): the collection viewer sends no public
// page-view notification at all today, so there is nothing to hold back until the viewer signs in.
// If one is ever added to this tree, it must be gated the same way the board's is.
export const PublicShowcaseRequireAccountGate = memo(
  ({ children, requireAccount }: PublicShowcaseRequireAccountGateProps) => {
    const [showAnonymousSignUpModal] = useAirModal(PublicShowcaseAnonymousSignUpModal);

    const handleMissingSession = useCallback(() => {
      showAnonymousSignUpModal({});
    }, [showAnonymousSignUpModal]);

    useOnMissingUserSession({
      onMissingSession: handleMissingSession,
      enabled: requireAccount,
    });

    return <>{children}</>;
  },
);

PublicShowcaseRequireAccountGate.displayName = 'PublicShowcaseRequireAccountGate';
